import {CommentType} from "../../definitions/types";
import {getTimeAgo, catalogName} from "../../utils/helpers";

export class CommentModel {
    private readonly _id: number;
    private readonly _ratingId: number;
    private readonly _internal: boolean;
    private readonly _name?: string;
    private readonly _text: string;
    private readonly _createdOn: Date;

    constructor(commentType: CommentType) {
        this._id = commentType.id;
        this._ratingId = commentType.referenceId;
        this._internal = commentType.internal;
        this._name = commentType.name;
        this._text = commentType.text;
        this._createdOn = commentType.created;
    }

    /**
     * @method id
     * @description Returns the id of the comment.
     * @returns {number}
     */
    public get id(): number {
        return this._id;
    }

    /**
     * @method ratingId
     * @description Returns the id of the rating the comment belongs to.
     * @returns {number}
     */
    public get ratingId(): number {
        return this._ratingId;
    }

    /**
     * @method internal
     * @description Returns whether the comment is internal.
     * @returns {boolean}
     */
    public get internal(): boolean {
        return this._internal;
    }

    /**
     * @method name
     * @description Returns the name of the comment author, if available, otherwise returns 'Anonymous'.
     * @returns {string}
     */
    public get name(): string {
        return this._name ?? 'Anonymous';
    }

    /**
     * @method text
     * @description Returns the text of the comment.
     * @returns {string}
     */

    public get text(): string {
        return this._text;
    }

    /**
     * @method created
     * @description Returns the date the comment was created on.
     * @returns {Date}
     */
    public get created(): Date {
        return new Date(this._createdOn);
    }

    /**
     * @method createdOn
     * @description Returns the date the comment was created on in a human readable format.
     * @returns {string}
     */
    public get createdOn(): string {
        return this.created.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        })
    }

    /**
     * @method createdAgo
     * @description Returns the date the comment was created on in a human readable format.
     * @returns {string}
     */
    public get createdAgo(): string {
        return getTimeAgo(this._createdOn);
    }

    /**
     * @method byline
     * @description Returns the byline for the comment.
     * @returns {string}
     */
    public get byline(): string {
        return `Commented ${this.createdAgo} by ${this._internal ? catalogName : this._name}`;
    }
}